import type { Plan } from '@parcel/types';
import { Button } from '@parcel/ui';
import { UpgradeButton } from './upgrade-button.js';

export type SeatTone = 'ok' | 'at' | 'over';

/** Seat usage tone. `at` = exactly at the limit (full), `over` = grandfathered above it. */
export function seatBannerState(used: number, limit: number): SeatTone {
  if (used > limit) return 'over';
  if (used >= limit) return 'at';
  return 'ok';
}

/** The plan to upsell for more seats: Free buys Starter, any paid plan buys Pro. */
export function nextSeatPlan(current: Plan): Plan {
  return current === 'free' ? 'starter' : 'pro';
}

export function SeatBanner({
  used,
  limit,
  workspaceSlug,
  canManageSeats = false,
  onManageSeats,
  manageDisabled = false,
}: {
  used: number;
  limit: number;
  /** Retained for call-site context; UpgradeButton resolves plan via useEntitlements. */
  effectivePlan: Plan;
  workspaceSlug: string;
  canManageSeats?: boolean;
  onManageSeats?: () => void;
  manageDisabled?: boolean;
}) {
  const tone = seatBannerState(used, limit);
  if (tone === 'ok') return null;
  const message =
    tone === 'over'
      ? `You are over your plan's seat limit (${used}/${limit}). Existing members keep access; add seats to invite more.`
      : `You have used all ${limit} ${limit === 1 ? 'seat' : 'seats'}. Add seats to invite more.`;
  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-400/25 dark:bg-amber-400/10 dark:text-amber-300">
      <span>{message}</span>
      {canManageSeats ? (
        <Button type="button" size="sm" onClick={onManageSeats} disabled={manageDisabled}>
          Add seats
        </Button>
      ) : (
        <UpgradeButton workspaceSlug={workspaceSlug} gate="seat_limit" />
      )}
    </div>
  );
}
